import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { PaymentAccountSelect } from '@/components/travel/accounting/payment-account-select';

interface SupplierPaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: {
    id: string;
    name: string;
    totalCost: number;
    supplierPaidAmount: number | null;
    currency: string;
    partner?: { companyName: string } | null;
  } | null;
  onSuccess?: () => void;
}

export function SupplierPaymentModal({
  open,
  onOpenChange,
  item,
  onSuccess,
}: SupplierPaymentModalProps) {
  const { t } = useTranslation(['bookings', 'common']);
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('BANK_TRANSFER');
  const [accountId, setAccountId] = useState<string | undefined>(undefined);
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  const remaining = item ? item.totalCost - (item.supplierPaidAmount || 0) : 0;

  useEffect(() => {
    if (open && item) {
      setAmount(remaining.toFixed(2));
      setReference('');
      setNotes('');
      setPaymentMethod('BANK_TRANSFER');
      setAccountId(undefined);
    }
  }, [open, item, remaining]);

  const utils = trpc.useUtils();
  const mutation = trpc.booking.recordSupplierItemPayment.useMutation({
    onSuccess: () => {
      showSuccessToast(t('items.supplierPaymentSuccess') || 'Supplier payment recorded');
      utils.booking.byId.invalidate();
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error) => showErrorToast(error.message),
  });

  const handleSubmit = () => {
    if (!item) return;
    mutation.mutate({
      bookingItemId: item.id,
      amount: parseFloat(amount) || 0,
      paymentMethod: paymentMethod as any,
      currency: item.currency,
      reference: reference || undefined,
      notes: notes || undefined,
      accountId,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('items.paySupplierTitle') || 'Supplier Payment'}</DialogTitle>
          <DialogDescription>
            {item?.partner?.companyName} — {item?.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-muted space-y-1 rounded-lg p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('items.totalCost') || 'Total Cost'}</span>
              <span className="font-medium">
                {item?.currency} {item?.totalCost.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t('items.alreadyPaid') || 'Already Paid'}
              </span>
              <span>
                {item?.currency} {(item?.supplierPaidAmount || 0).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between border-t pt-1 font-medium">
              <span>{t('items.remainingDebt') || 'Remaining'}</span>
              <span className="text-red-600">
                {item?.currency} {remaining.toFixed(2)}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('items.paymentAmount') || 'Amount'}</Label>
            <Input
              type="number"
              step="0.01"
              max={remaining}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('items.paymentMethod') || 'Payment Method'}</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BANK_TRANSFER">
                  {t('items.methods.bankTransfer') || 'Bank Transfer'}
                </SelectItem>
                <SelectItem value="CASH">{t('items.methods.cash') || 'Cash'}</SelectItem>
                <SelectItem value="CREDIT_CARD">
                  {t('items.methods.creditCard') || 'Credit Card'}
                </SelectItem>
                <SelectItem value="CHEQUE">{t('items.methods.cheque') || 'Cheque'}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Cash / Bank Account (optional — auto by method mapping) */}
          <PaymentAccountSelect value={accountId} onChange={setAccountId} />

          <div className="space-y-2">
            <Label>{t('items.reference') || 'Reference'}</Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder={t('items.referencePlaceholder') || 'Receipt/Reference no...'}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('items.notes') || 'Notes'}</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('items.optionalNotes') || 'Optional notes...'}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('common:cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={mutation.isPending || !amount || parseFloat(amount) <= 0}
          >
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('items.confirmPayment') || 'Record Payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
